import * as React from "react";

import composeRefs from "@seznam/compose-react-refs";
import { CommandInput } from "@v1/components/designSystem/shadcn/core/command";

import { useCombobox } from "../hooks/useCombobox";

type ComboboxInputProps = {
  startEnhancer?: React.ReactNode;
  endEnhancer?: React.ReactNode;
  placeholder?: string;
  outlineOnActive?: boolean;
  size?: "sm" | "base";
  controlledOnInputChange?: (value: string) => void;
  controlledInput?: string;
};

export const ComboboxInput = React.forwardRef<
  HTMLInputElement,
  ComboboxInputProps
>(
  (
    {
      startEnhancer,
      endEnhancer,
      placeholder,
      outlineOnActive = true,
      size = "base",
      controlledOnInputChange,
      controlledInput,
      ...props
    },
    ref,
  ) => {
    const {
      open,
      triggerInputRef,
      input,
      setInput,
      value,
      isUsingTriggerInput,
      setIsUsingTriggerInput,
      syncInputWithSelectedValue,
      setIsControlledSearch,
    } = useCombobox();

    // biome-ignore lint/correctness/useExhaustiveDependencies: Disable exhaustive deps for now
    React.useEffect(() => {
      setIsUsingTriggerInput(true);

      // NOTE (Max, 2025-01-10): If the combobox already has a value, we make sure
      // that the input has that value: ensures the combobox's value is in sync with
      // the input's value.
      if (syncInputWithSelectedValue && !input && typeof value === "string") {
        setInput(value);
      }
      if (controlledOnInputChange) {
        setIsControlledSearch(true);
      }
    }, []);

    /**
     * NOTE (Max, 2025-01-04): this handleBlur is used to solve the following situation:
     * 1. The combobox has a selected value.
     * 2. The user starts typing a new value.
     * 3. The user clicks outside the combobox (without having selected a new value).
     *
     * In this case, we want to reset the input to the already-selected
     * value, hence this handleBlur. Otherwise, the input would have still shown the
     * incomplete string that the user was typing (while the actual selected value wouldn't have
     * actually changed. That way, we ensure that the input & the selected value are in sync.
     */
    const handleBlur = () => {
      if (
        syncInputWithSelectedValue &&
        !open &&
        isUsingTriggerInput &&
        typeof value === "string"
      ) {
        setInput(value);
      }
    };

    return (
      <CommandInput
        ref={composeRefs(ref, triggerInputRef)}
        value={controlledInput ?? input}
        onValueChange={
          controlledOnInputChange ? controlledOnInputChange : setInput
        }
        placeholder={placeholder}
        variant={size}
        startEnhancer={startEnhancer}
        endEnhancer={endEnhancer}
        onBlur={handleBlur}
        outlineOnActive={outlineOnActive}
        {...props}
      />
    );
  },
);

ComboboxInput.displayName = "ComboboxInput";
